import { Col, Row } from "reactstrap";
import { SongUb } from "../../gql/graphql";
import { ItemCard } from "./ItemCard";
import { TextInputGraphQlMutate } from "./TextInputGraphQlMutate";
import { SelectGraphQlMutate } from "./SelectGraphQlMutate";
import { CreatableSelectGraphQlMutate } from "./CreatableSelectGraphQlMutate";
import { graphql } from "../../gql";
import { tonalities } from "../../common/constants/tonalities";
import { iso639_3Codes } from "../../common/constants/iso-639-3-codes";
import { useTranslation } from "react-i18next";

interface Props {
  song: SongUb;
  isDisabled?: boolean;
  isTitleDisabled?: boolean;
  onMutationCompleted?: () => void;
}

const upsertSongMutation = graphql(`
  mutation upsertSong($id: ID!, $title: String!, $bucket: String!) {
    upsertSong(id: $id, title: $title, bucket: $bucket) {
      ...SongFields
    }
  }
`);

const updateAuthorMutation = graphql(`
  mutation updateAuthor($author: String, $bucket: String!, $id: ID!) {
    updateAuthor(author: $author, bucket: $bucket, id: $id) {
      ...SongFields
    }
  }
`);

const updateLicenseMutation = graphql(`
  mutation updateLicense($bucket: String!, $id: ID!, $license: String) {
    updateLicense(bucket: $bucket, id: $id, license: $license) {
      ...SongFields
    }
  }
`);

const updateTonalityMutation = graphql(`
  mutation updateTonality($id: ID!, $tonality: String, $bucket: String!) {
    updateTonality(id: $id, tonality: $tonality, bucket: $bucket) {
      ...SongFields
    }
  }
`);

const updateLanguageMutation = graphql(`
  mutation updateLanguage($id: ID!, $language: String, $bucket: String!) {
    updateLanguage(id: $id, language: $language, bucket: $bucket) {
      ...SongFields
    }
  }
`);

const updateTagsMutation = graphql(`
  mutation updateTags($id: ID!, $tags: [String!]!, $bucket: String!) {
    updateTags(id: $id, tags: $tags, bucket: $bucket) {
      ...SongFields
    }
  }
`);

export function AboutSongCard({
  song,
  isDisabled = false,
  isTitleDisabled = false,
  onMutationCompleted,
}: Props) {
  const { t } = useTranslation();

  return (
    <ItemCard title={song.title}>
      <TextInputGraphQlMutate
        labelValue={t("items.about.title")}
        htmlTagIdAndName={"title"}
        value={song.title}
        mutationDocument={upsertSongMutation}
        buildVariables={(validatedValue) => ({
          id: song.id,
          title: validatedValue as string,
          bucket: song.bucket,
        })}
        isRequired={true}
        autoFocus={true}
        isDisabled={isTitleDisabled}
        onMutationCompleted={onMutationCompleted}
      />

      <CreatableSelectGraphQlMutate
        labelValue={t("items.about.tags")}
        htmlTagIdAndName="tags"
        initialValues={song.tags}
        bucket={song.bucket}
        mutationDocument={updateTagsMutation}
        buildVariables={(values: readonly string[]) => ({
          id: song.id,
          tags: [...values],
          bucket: song.bucket,
        })}
        placeholder={t("items.about.tagsPlaceholder")}
        helpText={t("items.about.tagsHelp")}
        isDisabled={isDisabled}
      />

      <Row>
        <Col xs={12} xxl={6}>
          <SelectGraphQlMutate
            labelValue={t("items.about.tonality")}
            htmlTagIdAndName={"tonality"}
            value={song.tonality ?? null}
            mutationDocument={updateTonalityMutation}
            buildVariables={(tonality) => ({
              id: song.id,
              tonality,
              bucket: song.bucket,
            })}
            options={tonalities}
            isDisabled={isDisabled}
            placeholder={t("items.about.tonalityPlaceholder")}
          />
        </Col>
        <Col>
          <SelectGraphQlMutate
            labelValue={t("items.about.language")}
            htmlTagIdAndName={"language"}
            value={song.language ?? null}
            mutationDocument={updateLanguageMutation}
            buildVariables={(language) => ({
              id: song.id,
              language,
              bucket: song.bucket,
            })}
            options={iso639_3Codes}
            isDisabled={isDisabled}
            placeholder={t("items.about.languagePlaceholder")}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12} xxl={6}>
          <TextInputGraphQlMutate
            labelValue={t("items.about.author")}
            htmlTagIdAndName={"author"}
            value={song.author ?? null}
            mutationDocument={updateAuthorMutation}
            buildVariables={(validatedValue) => ({
              author: validatedValue,
              bucket: song.bucket,
              id: song.id,
            })}
            isRequired={false}
            isDisabled={isDisabled}
          />
        </Col>
        <Col>
          <TextInputGraphQlMutate
            labelValue={t("items.about.license")}
            htmlTagIdAndName={"license"}
            value={song.license ?? null}
            mutationDocument={updateLicenseMutation}
            buildVariables={(validatedValue) => ({
              bucket: song.bucket,
              id: song.id,
              license: validatedValue,
            })}
            isRequired={false}
            isDisabled={isDisabled}
          />
        </Col>
      </Row>
    </ItemCard>
  );
}
